import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import generateCaptcha from "../../components/captcha";
import formatDateTime from "../../components/formatDateTime";
import FileUpload from "../../components/ul/FileUpload";
import UploadButton from "../../components/ul/UploadButton";
import DeleteButton from "../../components/ul/DeleteButton";
import NotFound from "../../components/ul/NotFound";
import {
  imageSizeExceededMessage,
  imageType,
  invalidImageMessage,
  MAX_FILE_SIZE_IMAGE,
} from "../../components/imageType";
import {
  LuCalendarClock,
  LuPlay,
  LuPause,
  LuSend,
  LuPencil,
  LuX,
} from "react-icons/lu";

const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const INTERVAL_PRESETS = [
  { label: "1 min", value: 1, unit: "minute" },
  { label: "5 min", value: 5, unit: "minute" },
  { label: "15 min", value: 15, unit: "minute" },
  { label: "30 min", value: 30, unit: "minute" },
  { label: "1 hour", value: 1, unit: "hour" },
  { label: "2 hours", value: 2, unit: "hour" },
  { label: "6 hours", value: 6, unit: "hour" },
  { label: "12 hours", value: 12, unit: "hour" },
];

const emptyForm = {
  title: "",
  message: "",
  url: "",
  schedule_type: "daily",
  send_at: "",
  interval_value: 15,
  interval_unit: "minute",
  time: "09:00",
  day_of_week: 1,
  day_of_month: 1,
};

const scheduleSummary = (t) => {
  switch (t.schedule_type) {
    case "once":
      return t.send_at ? `Once · ${formatDateTime(t.send_at)}` : "Once";
    case "interval":
      return `Every ${t.interval_value} ${t.interval_unit}${t.interval_value > 1 ? "s" : ""}`;
    case "weekly":
      return `Weekly · ${WEEK[t.day_of_week] || "?"} · ${t.time}`;
    case "monthly":
      return `Monthly · day ${t.day_of_month} · ${t.time}`;
    default:
      return `Daily · ${t.time}`;
  }
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const NotificationTemplates = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.GetNotificationTemplates();
      if (resp?.status) setData(resp.data || []);
      else toast.error(resp?.message);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const pickInterval = (value, unit) => setForm({ ...form, interval_value: value, interval_unit: unit });

  const onFile = (e) => {
    const f = e.target.files[0];
    if (f && !imageType.includes(f.type)) {
      toast.error(invalidImageMessage);
      setFile(null);
      e.target.value = "";
      return;
    }
    if (f?.size > MAX_FILE_SIZE_IMAGE) {
      toast.error(imageSizeExceededMessage);
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setFile(null);
    setEditingId(null);
  };

  const onEdit = (t) => {
    setForm({
      title: t.n_title || "",
      message: t.n_message || "",
      url: t.n_url || "",
      schedule_type: t.schedule_type || "daily",
      send_at: toLocalInput(t.send_at),
      interval_value: t.interval_value || 15,
      interval_unit: t.interval_unit || "minute",
      time: t.time || "09:00",
      day_of_week: t.day_of_week ?? 1,
      day_of_month: t.day_of_month ?? 1,
    });
    setFile(null);
    setEditingId(t._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error("Title and message are required");
      return;
    }
    if (form.schedule_type === "once" && !form.send_at) {
      toast.error("Pick a date & time for a one-time notification");
      return;
    }
    setLoader(true);
    try {
      const fd = new FormData();
      Object.keys(form).forEach((k) => fd.append(k, form[k]));
      if (file) fd.append("image", file);
      const resp = editingId
        ? await Calling.UpdateNotificationTemplate(editingId, fd)
        : await Calling.CreateNotificationTemplate(fd);
      if (resp?.status) {
        toast.success(editingId ? "Template updated" : "Template scheduled");
        resetForm();
      } else {
        toast.error(resp?.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      fetchData();
      setLoader(false);
    }
  };

  const onToggle = async (id) => {
    const resp = await Calling.ToggleNotificationTemplate(id);
    resp?.status ? toast.success("Updated") : toast.error(resp?.message);
    fetchData();
  };

  const onSendNow = async (id) => {
    const resp = await Calling.SendTemplateNow(id);
    resp?.status ? toast.success("Sent now") : toast.error(resp?.message);
    fetchData();
  };

  const onDelete = async (id) => {
    if (!generateCaptcha("Delete this template? Solve to confirm.")) {
      toast.error("Captcha incorrect");
      return;
    }
    const resp = await Calling.DeleteNotificationTemplate({ id: [id] });
    resp?.status ? toast.success("Deleted") : toast.error(resp?.message);
    if (editingId === id) resetForm();
    fetchData();
  };

  const statusPill = (s) => {
    const map = {
      active: "bg-green-500/15 text-green-400 ring-1 ring-green-500/30",
      paused: "bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-500/30",
      completed: "bg-gray-500/15 text-gray-400 ring-1 ring-gray-500/30",
    };
    return `px-2.5 py-1 rounded-full text-xs font-semibold ${map[s] || map.completed}`;
  };

  const label = "block mb-1 text-sm font-medium text-main-subfont";
  const input = "ui-input w-full p-2 rounded-xl";

  return (
    <>
      {loader && <Loader loader={loader} />}
      <div className="ml-0 md:ml-56 text-main-font mt-16 px-3 pb-10">
        {/* ---- Form card ---- */}
        <div className="ui-card-solid w-full mt-4 p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30">
              <LuCalendarClock size={22} />
            </div>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-extrabold">
                {editingId ? "Edit Template" : "New Notification Template"}
              </h1>
              <p className="text-xs sm:text-sm text-main-subfont">
                Compose once, schedule it, and let it send automatically
              </p>
            </div>
            {editingId && (
              <button
                onClick={resetForm}
                className="flex items-center gap-1 px-3 py-2 rounded-xl bg-black/5 hover:bg-black/10 text-sm"
              >
                <LuX size={16} /> Cancel
              </button>
            )}
          </div>
          <hr className="border-main-border mb-4" />

          <form
            onSubmit={onSubmit}
            encType="multipart/form-data"
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div>
              <label className={label}>Title</label>
              <input name="title" value={form.title} onChange={onChange} className={input} placeholder="e.g. Daily Bonus is live!" required />
            </div>
            <div>
              <label className={label}>URL (optional)</label>
              <input name="url" value={form.url} onChange={onChange} className={input} placeholder="https://…" />
            </div>
            <div className="md:col-span-2">
              <label className={label}>Message</label>
              <input name="message" value={form.message} onChange={onChange} className={input} placeholder="Short body text" required />
            </div>
            <div className="md:col-span-2">
              <label className={label}>Image {editingId ? "(leave empty to keep current)" : "(optional)"}</label>
              <FileUpload id="image" name="image" accept="image/*" onChange={onFile} required={false} multiple={false} />
            </div>

            {/* Schedule */}
            <div className="md:col-span-2 bg-main-bg border border-main-border rounded-2xl p-4">
              <label className={label}>Schedule</label>
              <select name="schedule_type" value={form.schedule_type} onChange={onChange} className={`${input} mb-3`}>
                <option value="daily">Daily (at a time)</option>
                <option value="interval">Interval (every X minutes / hours)</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="once">One-time</option>
              </select>

              {form.schedule_type === "once" && (
                <div>
                  <label className={label}>Date &amp; time (IST)</label>
                  <input type="datetime-local" name="send_at" value={form.send_at} onChange={onChange} className={input} />
                </div>
              )}

              {form.schedule_type === "interval" && (
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-main-subfont">Every</span>
                    <input type="number" min="1" name="interval_value" value={form.interval_value} onChange={onChange} className="ui-input w-24 p-2 rounded-xl" />
                    <select name="interval_unit" value={form.interval_unit} onChange={onChange} className="ui-input p-2 rounded-xl">
                      <option value="minute">minute(s)</option>
                      <option value="hour">hour(s)</option>
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {INTERVAL_PRESETS.map((p) => {
                      const active =
                        Number(form.interval_value) === p.value && form.interval_unit === p.unit;
                      return (
                        <button
                          type="button"
                          key={p.label}
                          onClick={() => pickInterval(p.value, p.unit)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium ${
                            active ? "bg-teal-500 text-white" : "bg-black/5 hover:bg-black/10"
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {["daily", "weekly", "monthly"].includes(form.schedule_type) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={label}>Time (IST)</label>
                    <input type="time" name="time" value={form.time} onChange={onChange} className={input} />
                  </div>
                  {form.schedule_type === "weekly" && (
                    <div>
                      <label className={label}>Day of week</label>
                      <select name="day_of_week" value={form.day_of_week} onChange={onChange} className={input}>
                        {WEEK.map((d, i) => (
                          <option key={i} value={i}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {form.schedule_type === "monthly" && (
                    <div>
                      <label className={label}>Day of month</label>
                      <input type="number" min="1" max="31" name="day_of_month" value={form.day_of_month} onChange={onChange} className={input} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <UploadButton className="w-full sm:w-auto" />
            </div>
          </form>
        </div>

        {/* ---- List card ---- */}
        <div className="ui-card-solid w-full mt-5 p-4 md:p-6">
          <h2 className="text-base sm:text-lg font-bold mb-3">Scheduled Templates</h2>
          <div className="overflow-x-auto">
            <table className="w-full rounded-lg overflow-hidden">
              <thead className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
                <tr>
                  <th className="px-3 py-3 text-left text-sm font-medium uppercase">Title</th>
                  <th className="px-3 py-3 text-left text-sm font-medium uppercase hidden md:table-cell">Schedule</th>
                  <th className="px-3 py-3 text-left text-sm font-medium uppercase">Status</th>
                  <th className="px-3 py-3 text-left text-sm font-medium uppercase hidden md:table-cell">Sent</th>
                  <th className="px-3 py-3 text-left text-sm font-medium uppercase hidden lg:table-cell">Last sent</th>
                  <th className="px-3 py-3 text-left text-sm font-medium uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((t) => (
                    <tr key={t._id} className="border-b border-main-border hover:bg-main-bg">
                      <td className="p-2 font-medium">{t.n_title}</td>
                      <td className="p-2 hidden md:table-cell text-main-subfont">{scheduleSummary(t)}</td>
                      <td className="p-2"><span className={statusPill(t.status)}>{t.status}</span></td>
                      <td className="p-2 hidden md:table-cell">{t.sent_count || 0}</td>
                      <td className="p-2 hidden lg:table-cell text-main-subfont">{t.last_sent_at ? formatDateTime(t.last_sent_at) : "—"}</td>
                      <td className="p-2">
                        <div className="flex items-center gap-1.5">
                          <button title="Send now" onClick={() => onSendNow(t._id)} className="p-2 rounded-lg bg-teal-500/20 text-teal-300 hover:brightness-125"><LuSend size={16} /></button>
                          <button title="Edit" onClick={() => onEdit(t)} className="p-2 rounded-lg bg-black/5 hover:brightness-125"><LuPencil size={16} /></button>
                          {t.status !== "completed" && (
                            <button title={t.status === "active" ? "Pause" : "Resume"} onClick={() => onToggle(t._id)} className="p-2 rounded-lg bg-black/5 hover:brightness-125">
                              {t.status === "active" ? <LuPause size={16} /> : <LuPlay size={16} />}
                            </button>
                          )}
                          <DeleteButton onClick={() => onDelete(t._id)} />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center px-4 py-6"><NotFound /></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationTemplates;
